import { RefObject, useCallback, useEffect, useState } from 'react'

/** Longest a click waits for the popover content before opening anyway. */
const OPEN_WAIT_TIMEOUT = 300 // ms

/**
 * Open state for a popover whose content loads asynchronously. Opening is held
 * until the content is ready (or the timeout elapses), so the popover never
 * appears with a skeleton that is replaced a moment later. Once open it stays
 * open until closed, whatever `ready` does afterwards.
 */
export const useOpenWhenReady = (
  ready: boolean,
  activatorRef: RefObject<HTMLElement>,
) => {
  const [requested, setRequested] = useState(false)
  const [open, setOpen] = useState(false)

  const close = useCallback(() => {
    setRequested(false)
    setOpen(false)
  }, [])

  useEffect(() => {
    if (!requested || open) {
      return undefined
    }
    if (ready) {
      setOpen(true)
      return undefined
    }
    const timeout = window.setTimeout(() => setOpen(true), OPEN_WAIT_TIMEOUT)
    // The popover is not mounted while waiting, so its own dismiss handling is
    // inactive: cancel the pending open on Escape or a press outside the button.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
      }
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!activatorRef.current?.contains(event.target as Node)) {
        close()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      window.clearTimeout(timeout)
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [requested, ready, open, close, activatorRef])

  const toggle = useCallback(() => {
    if (requested) {
      close()
    } else {
      setRequested(true)
    }
  }, [requested, close])

  return { open, toggle, close }
}
